import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AFormat, Pupil, Answer, CardInterface } from '@df/interfaces';
import { TeacherService } from '@app/teacher/services/teacher.service';
import { FormBuilder, FormControl } from '@angular/forms';
import { MAIN_DOMAIN } from '@app/app.constants';

@Component({
    selector: 'app-exercise-puzzle',
    templateUrl: './exercise-puzzle.component.html',
    styleUrls: ['./exercise-puzzle.component.scss']
})
export class ExercisePuzzleComponent implements OnInit {
    @Input() cardInstruction: string;
    @Input() exercise: any;
    @Input() cardFormat: string;
    @Input() index: number;
    @Input() selectedIndex: number;
    @Input() isFinishCard: boolean = false;
    @Input() viewType: string = 'template';
    @Input() activeCard: CardInterface;
    @Input() isAccessCard: boolean = false;
    @Input() isAuthorCard: boolean = false;
    @Input() currentAnswer: string = '';
    @Input() answer: any;
    @Input() studentAnswer: any;
    @Input() studentId = null;
    @Input() correctOrNot = false;

    @Output() exerciseCorrect = new EventEmitter();
    @Output() answers = new EventEmitter();
    @Output() answered = new EventEmitter();
    @Output() exerciseCompleted = new EventEmitter();

    currentStudent: Pupil;
    questionAnswers: Answer[] = [];
    answerValue: string;
    answerControl: FormControl;
    correctAnswers: Array<Answer> = [];
    includeAnswerViewTypes: Array<string> = ['teacher', 'result_modal', 'analytics_modal', 'student_right'];
    rowsForExercise = Array(4).keys();
    cellsForExercise = Array(20).keys();
    correctAnswerCheckbox = true;
    showHint = false;
    buttons: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

    constructor(private teacherService: TeacherService, private formBuilder: FormBuilder) {}

    ngOnInit() {
        console.log(this.exercise);
        if (this.exercise) {
            this.showHint = this.exercise.hint !== this.cardInstruction;
            if (this.activeCard) {
                this.showHint = this.showHint && this.exercise.hint !== this.activeCard.card_text;
            }

            this.exercise.img = this.exercise.image_url
                ? `${MAIN_DOMAIN}${this.exercise.image_url}`
                : `${MAIN_DOMAIN}/media/exercise_images/${this.exercise.image_name}`;

            if (this.includeAnswerViewTypes.includes(this.viewType)) {
                this.currentStudent = this.teacherService.getCurrentStudent;
                this.correctAnswers = this.exercise.exercise_correct_answers || this.exercise.answers;
                const formDisabled =
                    !this.isAuthorCard ||
                    !this.isAccessCard ||
                    this.viewType === 'result_modal' ||
                    this.viewType === 'analytics_modal';

                if (this.exercise.answers || this.exercise.answer_by_question) {
                    let answerFormValue = this.correctAnswers[0].shortAnswer;
                    if (this.viewType === 'analytics_modal') {
                        answerFormValue = this.exercise.answer_by_question[0].student_answers[0].shortAnswer;
                    }

                    this.answerControl = this.formBuilder.control({
                        value: answerFormValue,
                        disabled: formDisabled
                    });

                    if (this.viewType === 'result_modal') {
                        this.answerControl.setValue(this.currentAnswer);
                    }

                    if (this.viewType === 'student_right') {
                        this.answerControl.setValue('');
                    }
                }

                this.checkPrevResults();
            }
        }
    }

    getWidth(): string {
        if (this.cardFormat === AFormat.A5_FORMAT) {
            return '200%';
        } else if (this.cardFormat === AFormat.A3_FORMAT) {
            return '150%';
        }
        return '100%';
    }

    checkAnswer(): boolean {
        if (this.correctAnswers && this.correctAnswers.length > 0) {
            return this.correctAnswers.some(i => this.checkSpace(i.shortAnswer, this.answerControl.value));
        }
        return false;
    }

    private checkSpace(correctAnswerValue: string, answerValue: string): boolean {
        const clearCorrectAnswerValue = correctAnswerValue.replace(/\s/g, '');
        const clearAnswerValue = answerValue.replace(/\s/g, '');
        return clearCorrectAnswerValue === clearAnswerValue;
    }

    changeAnswer(): void {
        const isCorrect = this.exercise.answers ? this.checkAnswer() : this.correctAnswerCheckbox;
        const answerResponse: Answer = {
            student: this.currentStudent ? this.currentStudent.id : this.studentId,
            fullAnswer: this.correctAnswers ? this.correctAnswers[0].fullAnswer : '',
            card: this.activeCard.id,
            shortAnswer: this.exercise.answers ? this.answerControl.value : '',
            exercise: this.exercise.id,
            correct: isCorrect,
            question: this.correctAnswers ? this.correctAnswers[0].question : null
        };

        if (this.correctAnswers) {
            this.correctAnswers.forEach(item => {
                if (item.shortAnswer === this.answerControl.value) {
                    answerResponse.fullAnswer = item.shortAnswer;
                    answerResponse.question = item.question;
                }
            });
        }

        this.questionAnswers = [];
        this.questionAnswers.push(answerResponse);

        this.answers.emit(this.questionAnswers);
        this.answered.emit(answerResponse);
        this.exerciseCorrect.emit(isCorrect);
        this.exerciseCompleted.emit(true);
        this.clearAnswer();
    }

    clearAnswer(): void {
        this.currentAnswer = '';
        this.answerControl.setValue('');
        // this.changeAnswer();
    }

    private checkPrevResults(): void {
        if (this.activeCard && this.activeCard.prev_results) {
            this.activeCard.prev_results.results.forEach(item => {
                if (item.exercise_id === this.exercise.id) {
                    if (this.exercise.answers) {
                        this.answerControl.setValue(item.short_answer);
                        return;
                    }
                    this.correctAnswerCheckbox = item.correct;
                }
            });
        }
    }

    public getCustomStyle(): any {
        if (this.exercise.img && this.exercise.img.includes('fingers')) {
            return { height: '60px' };
        }

        if (this.exercise.subject_pk === 2 && this.exercise.img && this.exercise.img.includes('dots')) {
            return { width: '100%', maxWidth: '156px', height: 'auto', maxHeight: '80px' };
        }

        if (this.exercise.img && this.exercise.img.includes('dots') && this.viewType !== 'print') {
            return { height: '30px' };
        }

        if (this.exercise.img && this.exercise.img.includes('dots') && this.viewType === 'print') {
            return { height: '70px', maxWidth: '165px' };
        }

        if (
            this.exercise.theme_pk === 24 &&
            this.exercise.img &&
            this.exercise.hint.includes('Напиши 3') &&
            this.viewType === 'print'
        ) {
            return { width: '100%', maxWidth: '140px', height: 'auto', maxHeight: '93px' };
        }

        if (this.exercise.theme_pk === 30 && this.exercise.img && this.viewType === 'print') {
            return { width: '100%', maxWidth: '200px', height: 'auto', maxHeight: '100px' };
        }
    }

    keyboardClicked(button: number | string) {
        this.currentAnswer += button.toString();
        this.answerControl.setValue(this.currentAnswer);
    }

    isCorrectAnswer(): boolean {
        return this.viewType === 'student_left' && this.isFinishCard && this.correctOrNot;
    }
}
